import { Contract, ServiceConfigOption } from '@/modules/Contract/types'
import { useTranslation } from 'react-i18next'
import { Typogaphy, Chip, IconButton, Popover } from 'vnetwork-ui'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import MakeCopyIcons from '@/icons/MakeCopyIcons'
import { AlertError } from 'vnetwork-ui'
import ViewConfig from '../ViewConfig'
import { formatCurrency } from '@/utils/function'
import MoreVert from '@/icons/MoreVert'
import CartIcons from '@/icons/CartIcons'
import { TFunction } from 'i18next'
import { useFormContext, useWatch } from 'react-hook-form'
interface ProductSummaryProps {
    isPOC?: boolean
    service: any
    product_group_name: string
    service_configoptions: ServiceConfigOption[]
    service_price_register: number
    hasErrors: boolean
    onEdit: () => void
    onDuplicate?: () => void
    onDelete: () => void
}
const renderHeaderForProductGroupName = (
    productGroupName: string,
    service: any,
    t: TFunction
) => {
    switch (productGroupName?.toLowerCase()) {
        case 'ssl':
        case 'domain':
            return (
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Typogaphy>
                        ({service?.service_domain_metadata?.domain_value})
                    </Typogaphy>
                </div>
            )
        default:
            return (
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Typogaphy>
                        {service?.service_configoptions?.length || 0}
                    </Typogaphy>
                    <Typogaphy className="text-slate-500">
                        {t('configurations')}
                    </Typogaphy>
                </div>
            )
    }
}

const ProductSummary: React.FC<ProductSummaryProps> = ({
    isPOC = false,
    product_group_name,
    service_configoptions,
    service_price_register,
    hasErrors,
    service,
    onEdit,
    onDuplicate,
    onDelete,
}) => {
    const { t } = useTranslation()

    const { control } = useFormContext<Contract>()

    const currency_code = useWatch({
        control,
        name: 'currency_code',
    })
    return (
        <div className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200 overflow-hidden">
            {/* Header */}
            <div className="px-3 py-2.5 bg-slate-50/50 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <CartIcons className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Typogaphy className="font-medium text-slate-800 text-sm truncate">
                                    {product_group_name}
                                </Typogaphy>
                                {renderHeaderForProductGroupName(
                                    product_group_name,
                                    service,
                                    t
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Chip
                            label={t('product')}
                            size="small"
                            color="primary"
                            variant="contained"
                            className="text-xs"
                        />
                        {hasErrors && (
                            <div onClick={onEdit} className="cursor-pointer">
                                <AlertError
                                    error={new Error(t('validate-warning'))}
                                    reloadButton={false}
                                />
                            </div>
                        )}
                        <Popover
                            trigger="click"
                            interactive={true}
                            placement="bottom-end"
                            render={(attrs) => (
                                <div
                                    className="bg-white shadow-lg rounded-md p-2 border min-w-[180px]"
                                    tabIndex={-1}
                                    {...attrs}
                                >
                                    <div className="flex flex-col gap-1">
                                        <button
                                            type="button"
                                            onClick={onEdit}
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-md transition-colors"
                                        >
                                            <EditIcons className="w-4 h-4" />
                                            {t('edit-product')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onDuplicate}
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-md transition-colors"
                                        >
                                            <MakeCopyIcons className="w-4 h-4" />
                                            {t('duplicate')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onDelete}
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-red-50 text-red-600 rounded-md transition-colors"
                                        >
                                            <DeleteOutlineIcons className="w-4 h-4" />
                                            {t('delete')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        >
                            <div>
                                <IconButton size="small">
                                    <MoreVert className="w-4 h-4" />
                                </IconButton>
                            </div>
                        </Popover>
                    </div>
                </div>
            </div>

            {service_configoptions?.length > 0 && (
                <div className="px-3 py-2">
                    <ViewConfig
                        service_configoptions={service_configoptions}
                        isPOC={isPOC}
                    />
                </div>
            )}

            {/* Pricing */}
            {!isPOC && (
                <div className="px-3 py-2.5 bg-emerald-50/50 border-t border-emerald-200/60">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 bg-emerald-100 rounded flex items-center justify-center flex-shrink-0">
                                <svg
                                    className="w-3.5 h-3.5 text-emerald-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <Typogaphy className="font-medium text-emerald-800 text-sm">
                                        {t('provisional')}
                                    </Typogaphy>
                                    <span className="text-xs text-emerald-600">
                                        •
                                    </span>
                                    <Typogaphy
                                        size="small"
                                        className="text-emerald-600 text-xs"
                                    >
                                        {t('estimated_total')}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <Typogaphy className="text-lg font-semibold text-emerald-700">
                                {formatCurrency(
                                    service_price_register,
                                    currency_code
                                )}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProductSummary
